import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useState } from "react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/landing/Reveal";
import { MarianQuote } from "@/components/MarianQuote";
import { openPrintCard } from "@/lib/printCard";
import { TWO_HEARTS, TWO_HEARTS_CARDS } from "@/data/christos";
import { ChristosEmblem, ChristosWitnesses, ChristosPrayerLinks } from "@/components/christos/ChristosShared";
import { PrayerAudio } from "@/components/prayers/PrayerAudio";
import { MonthlyDevotions } from "@/components/prayers/MonthlyDevotions";

export default function TwoHeartsPage() {
  const t = TWO_HEARTS;
  const [err, setErr] = useState("");
  const printConsecration = (layout) => setErr(openPrintCard({
    eyebrow: t.latin,
    title: "The United Hearts of Jesus and Mary",
    subtitle: t.consecration.title,
    image: t.hero,
    blocks: [{ label: t.consecration.title, text: t.consecration.text, italic: true }],
    note: t.feast,
    source: { label: t.consecration.sourceLabel, url: t.consecration.sourceUrl },
    back: { eyebrow: "Cor Iesu · Cor Mariae", name: "The Two Hearts", blessing: "Ave Maria, gratia plena, Dominus tecum." },
    footer: "Theotokos · Mater Dei — Ad Iesum per Mariam",
  }, layout));

  return (
    <main data-testid="two-hearts-page" className="bg-ivory">
      <Seo title="The Two Hearts — the Sacred Heart & the Immaculate Heart united · Theotokos · Mater Dei" siteName="Theotokos · Mater Dei" description={t.lede} />

      <section className="relative overflow-hidden bg-wine text-ivory">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(50% 50% at 30% 20%, rgba(220,38,38,0.35), transparent), radial-gradient(50% 50% at 75% 80%, rgba(212,175,55,0.4), transparent)" }} aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-8 lg:py-28">
          <Reveal>
            <ChristosEmblem prefix="two-hearts" centered />
            <Link to="/christ-the-king" data-testid="two-hearts-back" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.26em] text-ember/90 transition-colors hover:text-ember"><ArrowLeft className="h-3.5 w-3.5" />Christos</Link>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.32em] text-ember">{t.eyebrow}</p>
            <h1 className="mt-4 font-serif text-5xl font-semibold leading-none sm:text-6xl lg:text-7xl">{t.title}</h1>
            <p className="mt-3 font-serif text-xl italic text-ivory/80">{t.latin}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <figure className="mx-auto mt-10 max-w-md overflow-hidden rounded-[2rem] border border-ember/40 bg-white/5 p-3 shadow-[0_50px_90px_-40px_rgba(0,0,0,0.8)]">
              <img src={t.hero.src} alt={t.hero.alt} className="w-full rounded-2xl object-contain" />
            </figure>
            <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-ivory/85 sm:text-lg">{t.lede}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-8 lg:px-12" data-testid="two-hearts-pair">
        <div className="grid gap-8 md:grid-cols-2">
          {t.hearts.map((hh, i) => (
            <Reveal key={hh.key} delay={i * 0.08}>
              <article data-testid={`two-hearts-${hh.key}`} className="flex h-full flex-col overflow-hidden rounded-3xl border border-crimson/15 bg-white">
                <div className="flex h-64 items-center justify-center bg-gradient-to-b from-wine/[0.06] to-ember/[0.08] p-5">
                  <img src={hh.image.src} alt={hh.image.alt} className="max-h-full w-auto object-contain" />
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h2 className="font-serif text-2xl font-semibold text-wine sm:text-3xl">{hh.title}</h2>
                  <p className="mt-1 font-serif text-sm italic text-crimson/70">{hh.latin}</p>
                  <p className="mt-4 font-serif text-lg italic leading-snug text-crimson">{hh.scripture}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-charcoal/85 sm:text-base">{hh.text}</p>
                  <p className="mt-4 text-[9px] leading-relaxed text-slateblue">{hh.image.credit}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 pt-4 pb-6 text-center sm:px-8">
        <Reveal><p className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson">One love — the saints and the popes</p></Reveal>
      </section>
      {t.quotes.map((q, i) => (
        <MarianQuote key={i} tone="crimson" testid={`two-hearts-quote-${i + 1}`} {...q} />
      ))}

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-8" data-testid="two-hearts-consecration">
        <Reveal>
          <div className="rounded-3xl border border-ember/40 bg-gradient-to-b from-wine to-crimson px-7 py-10 text-center text-ivory shadow-[0_40px_80px_-40px_rgba(88,10,20,0.75)]">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">{t.consecration.title}</p>
            <p data-testid="two-hearts-prayer-text" className="mt-5 font-serif text-xl leading-relaxed sm:text-2xl">{t.consecration.text}</p>
            <a href={t.consecration.sourceUrl} target="_blank" rel="noreferrer" data-testid="two-hearts-prayer-source" className="mt-5 inline-block text-xs underline">{t.consecration.sourceLabel}</a>
            <PrayerAudio prayerId={t.consecration.id} prefix="two-hearts" />
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={() => printConsecration("card")} data-testid="two-hearts-print-card" className="inline-flex items-center gap-2 rounded-full bg-ember px-5 py-3 text-sm font-semibold text-wine transition-colors hover:bg-ivory"><Printer className="h-4 w-4" />Print holy card</button>
              <button type="button" onClick={() => printConsecration("page")} data-testid="two-hearts-print-page" className="inline-flex items-center gap-2 rounded-full border border-ember/60 px-5 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-white/5">Print full page</button>
            </div>
            {err && <p role="alert" data-testid="two-hearts-print-error" className="mt-3 text-sm text-ember">{err}</p>}
          </div>
          <p className="mt-6 text-center text-sm leading-relaxed text-slateblue">{t.feast}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-8" data-testid="two-hearts-cards">
        <Reveal>
          <p className="text-center font-mono text-[11px] uppercase tracking-[0.3em] text-crimson">Matching prayer cards</p>
          <h2 className="mt-3 text-center font-serif text-3xl font-semibold text-wine sm:text-4xl">A card for each Heart</h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-sm leading-relaxed text-charcoal/85 sm:text-base">Two matching cards — the Sacred Heart of Jesus and the Immaculate Heart of Mary — with an act of consecration and a short aspiration. Print each as a pocket holy card (two per page, with a back to cut and carry) or as a full page for the home.</p>
        </Reveal>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {TWO_HEARTS_CARDS.map((h, i) => (
            <Reveal key={h.key} delay={i * 0.08}>
              <article data-testid={`two-hearts-card-${h.key}`} className="flex h-full flex-col items-center rounded-3xl border border-crimson/15 bg-white p-6 text-center sm:p-8">
                <div className="flex h-56 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-wine/[0.05] to-ember/[0.07] p-4">
                  <img src={h.card.image.src} alt={h.card.image.alt} className="max-h-full w-auto object-contain" />
                </div>
                <h3 className="mt-5 font-serif text-2xl font-semibold text-wine">{h.heading}</h3>
                <p className="mt-1 font-serif text-sm italic text-crimson/70">{h.latin}</p>
                <PrayerAudio prayerId={`${h.key}-prayer`} prefix={`two-hearts-${h.key}`} />
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <button type="button" onClick={() => setErr(openPrintCard(h.card, "card"))} data-testid={`two-hearts-card-${h.key}-print-card`} className="inline-flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-sm font-semibold text-ivory transition-colors hover:bg-wine"><Printer className="h-4 w-4" />Print holy card</button>
                  <button type="button" onClick={() => setErr(openPrintCard(h.card, "page"))} data-testid={`two-hearts-card-${h.key}-print-page`} className="inline-flex items-center gap-2 rounded-full border border-crimson/30 px-5 py-2.5 text-sm font-semibold text-crimson transition-colors hover:border-ember">Full page</button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <p className="mt-4 text-center text-xs text-slateblue">Holy cards print two per page with a matching back — print double-sided and cut along the dashed line. Allow pop-ups if nothing appears.</p>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-24 sm:px-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slateblue">Sources</p>
        <ul className="mt-3 space-y-1.5">
          {t.sources.map((s, i) => (
            <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`two-hearts-source-${i}`} className="text-sm text-crimson underline-offset-2 hover:underline">{s.label}</a></li>
          ))}
        </ul>
      </section>
      <ChristosWitnesses ids={["montfort", "cyril"]} prefix="two-hearts" />
      <MonthlyDevotions prefix="two-hearts" />
      <ChristosPrayerLinks prefix="two-hearts" />
    </main>
  );
}
